// Global setup and cleanup for session isolation
// This file handles all global state that needs to persist across App component remounts

let globalSetupComplete = false;

// Centralized logging for critical actions
const logCriticalAction = (category, action, details = {}) => {
  const icons = {
    AUTH: '🔐',
    DATA: '💾',
    USER: '👤',
    ACTION: '🔧',
    CRITICAL: '⚡',
    ERROR: '🚨'
  };
    
  const icon = icons[category] || '📝';
  const timestamp = new Date().toISOString();
  const message = `${icon} ${category}: ${action}`;
    
  console.log(message, {
    timestamp,
    action,
    details,
    userEmail: window.currentUserEmail || 'Unknown',
    currentPage: window.currentNavigation || 'Unknown',
    userTeamId: window.currentUserTeamId || 'Unknown',
    appVersion: process.env.REACT_APP_VERSION || '0.1.1'
  });
};

// Smart console log capture system for problem reports
const consoleHistory = [];
const MAX_CONSOLE_LOGS = 50;
const duplicateCount = {};
let seen = new Set();

// Patterns to filter out noise
const NOISE_PATTERNS = [
  /Analytics: trackActivity called but tracking disabled/,
  /🔍 Open text buttons found: \[\]/,
  /🔍 Current stage config:.*/ // Only if repetitive
];

// High-value patterns to always capture
const HIGH_VALUE_PATTERNS = [
  /Error|Failed|Cannot|Unable/i,
  /🚨|❌|⚠️/,
  /Firebase.*Error/i,
  /Missing or insufficient permissions/i,
  /Auth.*failed/i,
  /Template.*not found/i,
  /User state/i,
  /Claims.*error/i,
  /📝 Template changing from/,
  /✅ User state set/,
  /👤 Fallback found user/,
  /🔐 AUTH:/,
  /🔄 SECURITY:/,
  /🔒 SECURITY:/,
  /🧭 NAV:/,
  /💾 DATA:/,
  /👤 USER:/,
  /🔧 ACTION:/,
  /⚡ CRITICAL:/
];

const shouldCaptureLog = (level, message) => {
  // Always capture errors
  if (level === 'error') return true;

  // Check for high-value patterns
  if (HIGH_VALUE_PATTERNS.some(pattern => pattern.test(message))) {
    return true;
  }

  // Filter out known noise
  if (NOISE_PATTERNS.some(pattern => pattern.test(message))) {
    return false;
  }

  // Limit duplicate messages
  const key = `${level}:${message.substring(0, 50)}`;
  duplicateCount[key] = (duplicateCount[key] || 0) + 1;
    
  // Allow first 2 occurrences, then only every 10th
  if (duplicateCount[key] <= 2) return true;
  if (duplicateCount[key] % 10 === 0) return true;
    
  return false;
};

const captureConsoleLog = (level, args) => {
  const message = args.map(arg => {
    if (typeof arg === 'object' && arg !== null) {
      try {
        // Reset seen set for each object
        seen = new Set();
                
        // Try to stringify, but handle circular references
        return JSON.stringify(arg, (key, value) => {
          // Handle circular references by replacing with a string
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
              return '[Circular Reference]';
            }
            seen.add(value);
          }
          return value;
        }, 2);
      } catch (error) {
        // Fallback if JSON.stringify still fails
        return `[Object: ${arg.constructor?.name || 'Unknown'}]`;
      }
    }
    return String(arg);
  }).join(' ');

  if (!shouldCaptureLog(level, message)) return;

  const timestamp = new Date().toISOString();
  const count = duplicateCount[`${level}:${message.substring(0, 50)}`] || 1;
    
  consoleHistory.push({
    level,
    message: count > 1 ? `${message} (x${count})` : message,
    timestamp
  });
    
  // Keep only the last MAX_CONSOLE_LOGS entries
  if (consoleHistory.length > MAX_CONSOLE_LOGS) {
    consoleHistory.shift();
  }
};

// Global error handler to suppress ResizeObserver errors
const suppressResizeObserverError = () => {
  const originalError = console.error;
  console.error = (...args) => {
    if (args[0]?.includes?.('ResizeObserver loop completed with undelivered notifications') ||
            args[0]?.includes?.('ResizeObserver loop limit exceeded')) {
      return; // Suppress ResizeObserver errors
    }
    originalError(...args);
  };

  // Also handle uncaught errors
  window.addEventListener('error', (event) => {
    if (event.error?.message?.includes('ResizeObserver loop completed with undelivered notifications') ||
            event.error?.message?.includes('ResizeObserver loop limit exceeded')) {
      event.preventDefault();
      return false;
    }
  });
};

// Initialize global setup (idempotent)
export const initializeGlobalSetup = () => {
  if (globalSetupComplete) {
    console.log('🔧 Global setup already initialized, skipping...');
    return;
  }
    
  console.log('🔧 Initializing global setup...');
    
  // Console override (make idempotent)
  if (!window._consoleOverridden) {
    console.log('🔧 Setting up console capture system...');
        
    // Override console methods to capture logs
    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info
    };

    console.log = (...args) => {
      captureConsoleLog('log', args);
      originalConsole.log(...args);
    };

    console.error = (...args) => {
      // Don't capture ResizeObserver errors we're suppressing
      if (!args[0]?.includes?.('ResizeObserver loop')) {
        captureConsoleLog('error', args);
      }
      originalConsole.error(...args);
    };

    console.warn = (...args) => {
      captureConsoleLog('warn', args);
      originalConsole.warn(...args);
    };

    console.info = (...args) => {
      captureConsoleLog('info', args);
      originalConsole.info(...args);
    };
        
    window._consoleOverridden = true;
    console.log('✅ Console capture system initialized');
  }
    
  // Global error handlers (make idempotent)
  if (!window._errorHandlersAttached) {
    console.log('🔧 Setting up global error handlers...');
    suppressResizeObserverError();
    window._errorHandlersAttached = true;
    console.log('✅ Global error handlers initialized');
  }
    
  // Global logging function
  if (!window.logCriticalAction) {
    console.log('🔧 Setting up global logging function...');
    window.logCriticalAction = logCriticalAction;
    console.log('✅ Global logging function initialized');
  }
    
  globalSetupComplete = true;
  console.log('✅ Global setup complete');
};

// Cleanup user-specific global state (called on session change)
export const cleanupGlobalSetup = () => {
  console.log('🔧 Cleaning up user-specific global state...');
    
  // Clear user-specific globals
  window.currentUserEmail = null;
  window.currentUserTeamId = null;
    
  // Note: Don't remove console overrides, error handlers, or logging function
  // as they're shared across sessions and should persist
    
  console.log('✅ User-specific global state cleaned up');
};

// Export console history for debugging
export const getConsoleHistory = () => consoleHistory;

// Export logging function for use in components
export { logCriticalAction };
















